// frontend/src/components/game/containers/MapContainer/MapContainer.jsx
import React, { useState, useRef, useEffect } from 'react';
import useGameStore from "@store/useGameStore";
import useMapStore from "@store/useMapStore";
import { TILE_SIZE } from "@utils/constants";

// Capas
import TerrainLayer from "./TerrainLayer";
import HighlightLayer from './HighlightLayer';
import ObjectsLayer from "./ObjectsLayer";
import ActionLinesLayer from "./ActionLinesLayer";
import ActionIconsLayer from "./ActionIconsLayer";
import AnimationLayer from "./AnimationLayer";
// ❌ Ya no importamos PlannedActionsLayer

import feedback from "@components/ui/general/feedback";
import "./MapContainer.css";

export default function MapContainer() {
  const game = useGameStore.getState().game;
  const isResolutionPhase = useGameStore.getState().isResolutionPhase;
  const tiles = useMapStore.getState().selectTerrainTiles();

  // Track drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  
  if (!game?.map || tiles.length === 0) {
    feedback.warningOnce("[MapManager]Map data is not available yet.");
    return null;
  }

  const mapWidth = game.map.width ?? 0;
  const mapHeight = game.map.height ?? 0;

  const mapStyle = {
    width: `${mapWidth * TILE_SIZE}px`,
    height: `${mapHeight * TILE_SIZE}px`,
  };

  // Drag-to-scroll handlers
  const handleMouseDown = (e) => {
    if (!containerRef.current) return;

    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: containerRef.current.scrollLeft,
      scrollTop: containerRef.current.scrollTop
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;

    e.preventDefault();

    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;

    containerRef.current.scrollLeft = dragStartPos.current.scrollLeft - deltaX;
    containerRef.current.scrollTop = dragStartPos.current.scrollTop - deltaY;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Add global mouse up listener to handle dragging outside the container
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className="map-container"
      style={{
        ...mapStyle,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
        {/* Base layers always in same order */}
        <TerrainLayer tiles={tiles} mapWidth={mapWidth} />
        <HighlightLayer />
        <ActionLinesLayer />

        {/* Conditional layer ordering based on game mode */}
        {isResolutionPhase ? (
          <>
            {/* Cinematic mode: Action icons below units */}
            <ActionIconsLayer />
            <ObjectsLayer />
          </>
        ) : (
          <>
            {/* Normal mode: Units below action icons */}
            <ObjectsLayer />
            <ActionIconsLayer />
          </>
        )}

        {/* Visual effects always on top */}
        <AnimationLayer />
      </div>
  );
}